import { FC, useMemo, useState } from 'react';

import { AnalyticEventAction } from 'analytics';

import { Dialogs } from 'analytics/events/dialog';

import { trackFilterUsageAnalyticsEvent } from 'analytics/events/filter-usage';

import debounce from 'debounce-promise';

import { observer } from 'mobx-react';

import { useStores } from 'mobx/hooks/useStores';

import { LocationsFetcher } from 'fetchers/LocationsFetcher';

import { isSubStringCaseInsensitive, pluralize } from 'utils/StringUtils';

import PatientLocation from 'models/PatientLocation';

import SearchBar from 'views/Dashboard/SearchBar';
import { FilterName } from 'views/Filters/filters.types';

import { DEBOUNCE_DELAY } from 'views/Pages/CareManagement/CareManagement.hooks';

import { VisibilityToggleCell } from 'components/Patient/Table/VisibilityToggleCell';
import { defaultStringPropertyCompare, TableColumn, Table } from 'components/Table';
import { MessageDialog } from 'components/UIkit/atoms/Dialog';

import './ManagePracticeLocationsPage.scss';

interface Row {
  id: number;
  name: string;
  visible: boolean;
}

interface PendingHideLocation {
  locationId: number;
  patientCount: number;
}

export const ManagePracticeLocationsPage: FC = observer(() => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingHideLocation, setPendingHideLocation] = useState<PendingHideLocation | null>(null);
  const { locationsStore } = useStores();

  const updateLocation = (visible: boolean, locationId: number) => {
    const location = locationsStore.getLocationById(locationId);
    const update = location.copyWith({ visible });
    locationsStore.updateLocation(update);
  };

  const handleLocationVisibilityToggle = async (toggleValue: boolean, locationId: number) => {
    // for toggle to true (show location), update directly without checks
    if (toggleValue) {
      updateLocation(true, locationId);
      return;
    }

    const patientCount = await LocationsFetcher.checkLocationHasPatients(locationId);

    if (patientCount > 0) {
      // Show confirmation dialog if location has patients
      setPendingHideLocation({ locationId, patientCount });
    } else {
      // No patients, hide directly
      updateLocation(false, locationId);
    }
  };

  const handleConfirmHideLocation = () => {
    if (pendingHideLocation) {
      updateLocation(false, pendingHideLocation.locationId);
    }
    setPendingHideLocation(null);
  };

  const handleCancelHideLocation = () => {
    setPendingHideLocation(null);
  };

  const columns: TableColumn<Row>[] = [
    {
      Header: 'Location',
      accessor: 'name',
      sortType: defaultStringPropertyCompare('name')
    },
    {
      Header: '',
      accessor: 'visible',
      disableSortBy: true,
      Cell: (cellParams) => (
        <VisibilityToggleCell
          onChange={(e) =>
            handleLocationVisibilityToggle(e.target.checked, cellParams.row.original.id)
          }
          isVisible={cellParams.row.original.visible}
        />
      )
    }
  ];

  const onTrackSearchAnalyticsEvent = useMemo(() => {
    return debounce((searchQuery) => {
      trackFilterUsageAnalyticsEvent({
        action: !searchQuery ? AnalyticEventAction.Clear : AnalyticEventAction.SearchedByLocations,
        value: FilterName.SearchLocations
      });
    }, DEBOUNCE_DELAY);
  }, []);

  const filteredLocations = locationsStore.locations.items.filter((location: PatientLocation) =>
    isSubStringCaseInsensitive(location.name, searchTerm)
  );
  const rows = filteredLocations.map((location: PatientLocation) => {
    return {
      id: location.id,
      name: location.name,
      visible: location.visible
    };
  });

  const isConfirmDialogOpen = pendingHideLocation !== null;

  return (
    <div className="practice-locations-page">
      <SearchBar
        searchValue={searchTerm}
        onSearchChanged={(searchTerm) => {
          onTrackSearchAnalyticsEvent(searchTerm);
          setSearchTerm(searchTerm);
        }}
        label="Search Locations"
        className="searchbar"
      />
      <Table columns={columns} rowData={rows} />

      <MessageDialog
        id={Dialogs.HideLocationWithPatients}
        isOpen={isConfirmDialogOpen}
        handleClose={handleCancelHideLocation}
        title="Hide Location"
        primaryActionProps={{ text: 'Hide Location', onClick: handleConfirmHideLocation }}
        secondaryActionProps={{ text: 'Cancel', onClick: handleCancelHideLocation }}
      >
        <>
          This location is currently assigned to{' '}
          <strong>{pendingHideLocation?.patientCount}</strong>{' '}
          {pluralize('patient', pendingHideLocation?.patientCount || 0)}.
          <br />
          <br />
          These assignments will remain, but new tickets (including for these patients) must be
          assigned to a different location.
        </>
      </MessageDialog>
    </div>
  );
});
